import type { GamesItem } from "@seagames/contracts";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useSyncExternalStore } from "react";
import { createChoiceStore } from "./choice-store";
import { defaultGames } from "./default-games";
import { gamesListQuery } from "./queries";

export { CURRENT_GAMES_KEY } from "./choice-store";

/**
 * A games as the app knows it: `GET /games` rows, so besides the option
 * fields (`games_id`, `code`, `name`, `is_para`) a page also has the games'
 * `timezone` and dates without another request.
 */
export type CurrentGamesOption = GamesItem;

export interface CurrentGames {
	/** null until the games list has loaded (or when there is none). */
	gamesId: string | null;
	games: CurrentGamesOption | undefined;
	/** Every games, main games first (the `/games/options` order). */
	options: CurrentGamesOption[];
	/** Remember `id` on this device; every reader re-renders with it. */
	setGamesId(id: string): void;
	isLoading: boolean;
	/** Why the list could not load (no answer, or refused) — for `errorMessage()`. `gamesId` is null then. */
	error: unknown;
}

const store = createChoiceStore(typeof window === "undefined" ? undefined : window.localStorage);

/** Outside React (e.g. a test or a one-off handler): the same as `useCurrentGames().setGamesId`. */
export const setCurrentGamesId = (id: string): void => store.set(id);

const EMPTY: CurrentGamesOption[] = [];

/**
 * The games this device works in — the sidebar's games switch. The device's
 * choice when it still exists, otherwise the default (`defaultGames`: running
 * today → next to start → undated → ended last). Pages that list something
 * games-wide read `gamesId` from here; a URL `games` param, where a page has
 * one, still wins on that page.
 */
export function useCurrentGames(): CurrentGames {
	const list = useQuery(gamesListQuery());
	const chosen = useSyncExternalStore(store.subscribe, store.get, () => null);
	const options = list.data ?? EMPTY;
	const games = useMemo(
		() => options.find((g) => g.games_id === chosen) ?? defaultGames(options, new Date()),
		[options, chosen],
	);
	return {
		gamesId: games?.games_id ?? null,
		games,
		options,
		setGamesId: setCurrentGamesId,
		isLoading: list.isPending,
		error: list.error,
	};
}
